import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/auth';
import { useLocaleStore } from '@/store/locale';
import { fetchPreferences } from '@/lib/api';

/**
 * Hydrates the driver's SERVER-side UI language (User.locale) into i18next +
 * the local locale store once per authenticated load, so the language follows
 * the driver across devices — a fresh device picks up the server value instead
 * of the device-detected default.
 *
 * The server is the source of truth for cross-device; hydrateLocale switches
 * i18next WITHOUT writing back (setLocale, called from Settings, is the
 * write-back path). Shares the ['preferences'] query key with the Settings
 * page + useEvieVoiceSync so there's a single fetch.
 *
 * No-op when unauthenticated (the pre-auth language is device-detected in
 * i18n.ts).
 */
export function useLocaleSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?._id);
  const hydrateLocale = useLocaleStore((s) => s.hydrateLocale);

  const q = useQuery({
    queryKey: ['preferences'],
    queryFn: fetchPreferences,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    const locale = q.data?.locale;
    if (locale) hydrateLocale(userId ?? null, locale);
  }, [isAuthenticated, userId, q.data?.locale, hydrateLocale]);
}
